import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, setToken, getToken } from "@/api/client";

const AuthContext = createContext(undefined);

function readSessionIdFromUrl() {
  const hash = window.location.hash || "";
  const search = window.location.search || "";
  const m = hash.match(/session_id=([^&]+)/) || search.match(/session_id=([^&]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [needsRole, setNeedsRole] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      // 1) Returning from Emergent Google auth — exchange session_id FIRST.
      const sessionId = readSessionIdFromUrl();
      if (sessionId) {
        try {
          const res = await api("/auth/session", {
            method: "POST",
            headers: { "X-Session-ID": sessionId },
          });
          setToken(res.session_token); // web uses the Bearer session token (no cross-origin cookie)
          setUser(res.user);
          setNeedsRole(res.needs_role);
        } catch (e) {
          console.error("Google session exchange failed", e);
          setToken(null);
        } finally {
          window.history.replaceState(null, "", window.location.pathname);
          setLoading(false);
        }
        return;
      }
      // 2) Normal load — cookie (Google) or bearer (email/password) via /auth/me.
      try {
        const me = await api("/auth/me");
        setUser(me);
        setNeedsRole(!me.role);
      } catch (e) {
        // Only evict the stored token on genuine auth failures — keep it on
        // transient network errors so a valid session isn't dropped.
        if (e.status === 401 || e.status === 403) setToken(null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const signIn = useCallback(async (email, password) => {
    const res = await api("/auth/login", { formData: { username: email, password } });
    setToken(res.access_token);
    setUser(res.user);
    setNeedsRole(!res.user.role);
    return res.user;
  }, []);

  const signUp = useCallback(async (email, password, name, role, managerCode) => {
    await api("/auth/register", { body: { email, password, name, role, manager_code: managerCode } });
    return await signIn(email, password);
  }, [signIn]);

  const signInWithGoogle = useCallback(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  const completeProfile = useCallback(async (role, inviteCode) => {
    const u = await api("/auth/complete-profile", { method: "POST", body: { role, invite_code: inviteCode } });
    setUser(u);
    setNeedsRole(false);
    return u;
  }, []);

  const signOut = useCallback(async () => {
    try {
      await api("/auth/logout", { method: "POST" });
    } catch (e) {
      console.error("Logout request failed", e);
    }
    setToken(null);
    setUser(null);
    setNeedsRole(false);
  }, []);

  const value = useMemo(
    () => ({ user, needsRole, loading, signIn, signUp, signInWithGoogle, completeProfile, signOut }),
    [user, needsRole, loading, signIn, signUp, signInWithGoogle, completeProfile, signOut]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
